// Purchase Order Detail — Phase 1E-δ #2
//
// Aksi terpenting halaman ini: MENETAPKAN VENDOR. Selama PO masih
// PENDING_VENDOR, seluruh alur di belakangnya berhenti — barang tidak bisa
// diterima, invoice tidak bisa diajukan. Karena itu aksi ini ditaruh sebagai
// panel penuh di atas, bukan tombol di pojok.
//
// NOL KONSTANTA BISNIS. Status dan wewenang dibaca dari
// GET /purchasing/meta/po. UI bukan keamanan — setiap aksi tetap dijaga
// role guard + state machine di backend; di sini hanya supaya orang tidak
// menekan tombol yang pasti ditolak.
import React, { useEffect, useState, useCallback } from "react";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariVendor, ambilVendor, labelVendor } from "../../lib/sumberPilihan";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { ArrowLeft, RefreshCw, Store, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatDateTime, formatQty } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import { statusBadge } from "./PurchaseOrders";
import { mintaTeks } from "../../lib/dialogAplikasi";
import Hint from "../../components/etams/Hint";
import { dpStatusBadge, hitungPlafon } from "./VendorDPs";
import {
  getPO, getPOMeta, assignPOVendor, closePO, cancelPO, buatPRSisa,
  daftarTutupSebagian, ajukanTutupSebagian, putusTutupSebagian, batalkanTutupSebagian,
  daftarBatalTelat, ajukanBatalTelat, putusBatalTelat, batalkanBatalTelat, ajukanBatalPO,
  listGRNs, getGRNMeta,
  listInvoices, getInvoiceMeta, listDPs, getDPMeta, apiErrorMessage,
} from "../../lib/purchasingApi";
import { aksiBatalPO } from "../../lib/aksiPurchasing";

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tzForRegion } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [vendorId, setVendorId] = useState("");
  const [expected, setExpected] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [prices, setPrices] = useState({});   // po_item_id -> string

  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");

  // Penerimaan yang sudah tercatat untuk PO ini (tautan dua arah).
  const [grns, setGrns] = useState([]);
  const [grnPerm, setGrnPerm] = useState({});

  // Tagihan vendor untuk PO ini (#4b-3a). Wewenang + status PO yang boleh
  // ditagih dibaca dari /meta/invoice — nol tuple role di file ini.
  const [invoices, setInvoices] = useState([]);
  const [invPerm, setInvPerm] = useState({});
  const [invEligible, setInvEligible] = useState([]);

  // Uang muka untuk PO ini (#4b-3c). Pola sama dengan panel tagihan:
  // wewenang, status PO yang boleh diajukan uang muka, rasio plafon, DAN
  // status mana yang menahan plafon semuanya dari /meta/dp.
  const [dps, setDps] = useState([]);
  const [dpMeta, setDpMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPO(id);
      setPo(r.data);
      const p = {};
      (r.data.items || []).forEach(it => {
        p[it.id] = it.unit_price == null ? "" : String(it.unit_price);
      });
      setPrices(p);
      listGRNs({ po_id: id, page: 1, page_size: 50 })
        .then(res => setGrns(res.data.items || []))
        .catch((e) => { setGrns([]); toast.error(apiErrorMessage(e, "Daftar penerimaan barang gagal dimuat — muat ulang halaman.")); });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat PO."));
      setPo(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getPOMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info status PO.")));
    // Vendor TIDAK lagi dimuat seluruhnya (dulu 197 vendor aktif lawan plafon 200) —
    // kotak vendor di bawah mencari di server (PemilihCari, 28 Sep 2026).
    getGRNMeta()
      .then(r => setGrnPerm(r.data.permissions || {}))
      .catch((e) => { setGrnPerm({}); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
    // Meta invoice dipanggil lebih dulu dan daftar tagihan HANYA diambil
    // kalau meta bilang boleh membaca. Untuk CS/Gudang/Produksi meta-nya 403
    // (INVOICE_READ_ROLES), panel tidak dirender, dan tidak ada permintaan
    // kedua yang juga pasti ditolak.
    getInvoiceMeta()
      .then(r => {
        setInvPerm(r.data.permissions || {});
        setInvEligible(r.data.po_statuses_eligible || []);
        if (!(r.data.permissions || {}).read) return;
        return listInvoices({ po_id: id, page: 1, page_size: 50 })
          .then(res => setInvoices(res.data.items || []))
          .catch((e) => { setInvoices([]); toast.error(apiErrorMessage(e, "Daftar tagihan vendor gagal dimuat — muat ulang halaman.")); });
      })
      .catch(() => { setInvPerm({}); setInvEligible([]); });
    // Sama polanya: meta dibaca dulu, daftarnya hanya diambil kalau meta
    // bilang boleh. Untuk role di luar DP_READ_ROLES meta-nya 403, panel
    // tidak dirender, dan tidak ada permintaan kedua yang juga pasti ditolak.
    getDPMeta()
      .then(r => {
        setDpMeta(r.data);
        if (!(r.data.permissions || {}).read) return;
        return listDPs({ po_id: id, page: 1, page_size: 100 })
          .then(res => setDps(res.data.items || []))
          .catch((e) => { setDps([]); toast.error(apiErrorMessage(e, "Daftar uang muka vendor gagal dimuat — muat ulang halaman.")); });
      })
      .catch(() => { setDpMeta(null); setDps([]); });
  }, [id]);

  // Nama bahan datang ter-embed dari backend (po_items → materials).
  // Kalau suatu saat tidak ada, jatuh ke potongan id — jangan pura-pura tahu.
  const materialLabel = (it) => {
    const m = it.material;
    if (m) return m.code ? `${m.code} — ${m.name}` : m.name;
    return "—";
  };

  // BUSINESS-35: pengajuan tutup sebagian PO ini (dimuat terpisah; gagal muat = daftar kosong + toast).
  const [tutupSebagian, setTutupSebagian] = useState([]);
  const muatTutupSebagian = useCallback(async () => {
    try { const r = await daftarTutupSebagian(id); setTutupSebagian(r.data.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Pengajuan tutup sebagian gagal dimuat.")); }
  }, [id]);
  useEffect(() => { muatTutupSebagian(); }, [muatTutupSebagian]);

  // BUSINESS-53: pengajuan batal PO karena vendor telat (PO tanpa uang muka, lewat 7 hari kerja).
  const [batalTelat, setBatalTelat] = useState([]);
  const muatBatalTelat = useCallback(async () => {
    try { const r = await daftarBatalTelat(id); setBatalTelat(r.data.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Pengajuan batal (vendor telat) gagal dimuat.")); }
  }, [id]);
  useEffect(() => { muatBatalTelat(); }, [muatBatalTelat]);

  const runAction = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setCancelOpen(false);
      setReason("");
      load();
      muatTutupSebagian();
      muatBatalTelat();
    } catch (e) {
      toast.error(apiErrorMessage(e, failMsg));
    } finally {
      setBusy(false);
    }
  };

  const submitAssign = async () => {
    if (!vendorId) { toast.error("Vendor wajib dipilih."); return; }
    const items = (po.items || []).map(it => ({
      po_item_id: it.id,
      unit_price: Number(prices[it.id]),
    }));
    const belum = items.filter(it => !Number.isFinite(it.unit_price) || it.unit_price <= 0);
    if (belum.length > 0) {
      toast.error(
        `${belum.length} item belum punya harga satuan > 0. PO tidak boleh ` +
        `terbit membawa harga sementara.`);
      return;
    }
    await runAction(
      () => assignPOVendor(id, {
        vendor_id: vendorId,
        items,
        expected_delivery_date: expected || null,
        notes: assignNotes || null,
      }),
      "Vendor ditetapkan — PO terbit.",
      "Gagal menetapkan vendor.");
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="po-detail-loading">
        <div className="h-8 w-56 bg-muted rounded animate-pulse" />
        {[...Array(4)].map((_, i) =>
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-12 text-center text-muted-foreground text-sm" data-testid="po-detail-empty">
        PO tidak ditemukan — mungkin sudah dihapus, atau bukan bagian region Anda.
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/purchasing/po")}>
            Kembali ke daftar
          </Button>
        </div>
      </div>
    );
  }

  const perm = meta?.permissions || {};
  const b = statusBadge(meta, po.status);

  const pendingVendor = po.status === "PENDING_VENDOR";
  const canAssign = perm.assign_vendor && pendingVendor;
  // close/cancel: state machine backend yang memutuskan; ini hanya cermin.
  const canClose  = perm.close  && po.status === "FULLY_RECEIVED";
  // Inventaris A20: sisa belum diterima → PR baru lewat alur PR biasa (backorder). Hak dari backend (perm.pr_sisa).
  const sisaItems = (po.items || []).filter((it) => Number(it.qty_ordered) - Number(it.qty_received || 0) > 0);
  // K203 BUSINESS-59: PO pengganti (PR sisa) boleh dibuat sebelum tagihan lunas — juga sesudah sisa ditutup.
  const sisaDitutup = Boolean(po.sisa_ditutup) || (po.status === "CLOSED" && po.ditutup_sebagian);
  const canPRSisa = perm.pr_sisa && (po.status === "PARTIAL_RECEIVED" || sisaDitutup) && sisaItems.length > 0;
  // K203 BUSINESS-57: PO yang sudah dikirim ke vendor — Owner batal langsung, Admin Purchasing mengajukan.
  const btMenungguApa = batalTelat.find((t) => t.status === "DIAJUKAN");
  const { batalLangsung: canCancel, ajukanBatal: canAjukanBatal } = aksiBatalPO(po, perm, Boolean(btMenungguApa));
  const tsMenunggu = tutupSebagian.find((t) => t.status === "DIAJUKAN");
  const canAjukanTS = perm.tutup_sebagian_ajukan && po.status === "PARTIAL_RECEIVED" && !tsMenunggu && !po.sisa_ditutup;
  // BUSINESS-53: tombol muncul untuk PO ISSUED/PARTIAL_RECEIVED yang janji kirimnya sudah lewat; syarat hari kerja
  // ke-8, uang muka, dan penerimaan diputus backend (pesannya ditampilkan apa adanya). VONIS-27SEP-JAWABAN-10 butir 8:
  // PO yang sudah menerima sebagian → yang dibatalkan hanya sisanya (disetujui = tutup sebagian).
  const btMenunggu = batalTelat.find((t) => t.status === "DIAJUKAN");
  const janjiLewat = po.expected_delivery_date && new Date(`${po.expected_delivery_date}T23:59:59`) < new Date();
  const btSisa = po.status === "PARTIAL_RECEIVED";
  const canAjukanBT = perm.batal_telat_ajukan && ["ISSUED", "PARTIAL_RECEIVED"].includes(po.status) && janjiLewat
    && !btMenunggu && !po.sisa_ditutup;

  const estAssignTotal = (po.items || []).reduce((s, it) =>
    s + (Number(it.qty_ordered) || 0) * (Number(prices[it.id]) || 0), 0);

  // Progres alur — nomornya dari backend, bukan urutan yang ditebak UI.
  const flowSteps = (meta?.statuses || []).filter(s => s.flow !== null)
    .sort((a, c) => a.flow - c.flow);
  const curFlow = (meta?.statuses || []).find(s => s.code === po.status)?.flow ?? null;

  return (
    <div className="space-y-6" data-testid="po-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/purchasing/po")}
                  data-testid="po-detail-back" className="-ml-2 mb-1">
            <ArrowLeft className="h-4 w-4 mr-1" /> Daftar PO
          </Button>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {po.po_number} <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
            {po.ditutup_sebagian && <StatusBadge tone="neutral" data-testid="po-ditutup-sebagian">Ditutup sebagian</StatusBadge>}
            {po.sisa_ditutup && <StatusBadge tone="warning" data-testid="po-sisa-ditutup">Sisa ditutup — menunggu tagihan lunas</StatusBadge>}
          </h1>
          {b.description && (
            <p className="text-sm text-muted-foreground mt-1" data-testid="po-status-desc">
              {b.description}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="po-detail-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
        </Button>
      </div>

      {/* Progres alur */}
      {flowSteps.length > 0 && curFlow !== null && (
        <div className="flex flex-wrap gap-1 text-xs" data-testid="po-flow">
          {flowSteps.map(s => (
            <span key={s.code} title={s.description}
                  className={`px-2 py-1 rounded ${
                    s.flow < curFlow ? "bg-success-soft text-success-soft-foreground"
                    : s.flow === curFlow ? "bg-info-soft text-info-soft-foreground font-medium"
                    : "bg-muted text-muted-foreground"}`}>
              {s.flow < curFlow && "✓ "}{s.label}
            </span>
          ))}
        </div>
      )}

      {/* ===== AKSI TERPENTING: tetapkan vendor ===== */}
      {pendingVendor && (
        <Card className={`p-4 border-2 ${canAssign ? "border-warning/50 bg-warning-soft/40" : "border-border"}`}
              data-testid="po-assign-panel">
          <div className="flex items-center gap-2 mb-1">
            <Store className="h-5 w-5 text-warning" />
            <h2 className="font-semibold">Tetapkan vendor &amp; harga</h2>
          </div>

          {!canAssign ? (
            <p className="text-sm text-muted-foreground" data-testid="po-assign-forbidden">
              PO ini menunggu vendor, tapi penetapan vendor bukan wewenang Anda —
              hanya Admin Purchasing (dan Owner) yang boleh. Hubungi mereka supaya
              alurnya jalan.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                Sampai vendor ditetapkan, barang tidak bisa diterima dan tagihan
                tidak bisa diajukan.{" "}
                <Hint>Harga satuan tiap item wajib terisi lebih dari 0 — PO tidak boleh terbit membawa harga sementara.</Hint>
              </p>

              <div className="grid gap-3 sm:grid-cols-2 mb-3">
                <div className="block">
                  <span className="text-sm">Vendor <span aria-hidden="true">*</span></span>
                  <div className="mt-1">
                    <PemilihCari value={vendorId || null} ambilSatu={ambilVendor}
                                 onChange={(v) => setVendorId(v?.id || "")}
                                 cari={(kata) => cariVendor(kata, { is_active: true })}
                                 label={labelVendor} keterangan={(v) => v?.phone || v?.contact_person || ""}
                                 tampilAwal placeholder="— pilih vendor —"
                                 placeholderCari="Ketik nama atau kode vendor…"
                                 testId="po-assign-vendor-select" />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Vendornya belum terdaftar?{" "}
                    <button type="button" className="underline underline-offset-2"
                            onClick={() => navigate("/purchasing/vendors")}>
                      Daftarkan dulu di menu Vendor.
                    </button>
                  </span>
                </div>
                <label className="block">
                  <span className="text-sm">Perkiraan tanggal kirim</span>
                  <Input type="date" value={expected} className="mt-1"
                         data-testid="po-assign-expected"
                         onChange={(e) => setExpected(e.target.value)} />
                </label>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Harga satuan per item</div>
                {(po.items || []).map(it => (
                  <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-6 text-sm">
                      {materialLabel(it)}
                      <span className="text-muted-foreground">
                        {" "}· {formatQty(it.qty_ordered, it.unit_of_measure)}
                      </span>
                      {/* Inventaris: harga beli terakhir bahan ini (GRN terakhir) — PETUNJUK, bukan isian otomatis. */}
                      {Number(it.material?.last_purchase_price) > 0 && (
                        <div className="text-xs text-muted-foreground" data-testid={`po-harga-terakhir-${it.id}`}>
                          Harga terakhir: {formatIDR(it.material.last_purchase_price)}
                          {it.material.last_purchase_vendor?.name ? ` · ${it.material.last_purchase_vendor.name}` : ""}
                          {it.material.last_purchase_at ? ` · ${String(it.material.last_purchase_at).slice(0, 10)}` : ""}
                        </div>
                      )}
                    </div>
                    <Input className={`col-span-4 ${
                             !(Number(prices[it.id]) > 0) ? "border-warning bg-warning-soft" : ""}`}
                           type="number" min="0" step="any" placeholder="Harga satuan *"
                           value={prices[it.id] ?? ""}
                           data-testid={`po-assign-price-${it.id}`}
                           onChange={(e) => setPrices(p => ({ ...p, [it.id]: e.target.value }))} />
                    <div className="col-span-2 text-right text-sm text-muted-foreground">
                      {formatIDR((Number(it.qty_ordered) || 0) * (Number(prices[it.id]) || 0))}
                    </div>
                  </div>
                ))}
                <div className="text-right text-sm pt-1">
                  Total PO: <span className="font-semibold">{formatIDR(estAssignTotal)}</span>
                </div>
              </div>

              <label className="block mt-3">
                <span className="text-sm">Catatan</span>
                <Textarea value={assignNotes} rows={2} className="mt-1"
                          data-testid="po-assign-notes"
                          onChange={(e) => setAssignNotes(e.target.value)} />
              </label>

              <div className="flex justify-end mt-3">
                <BusyButton onClick={submitAssign} busy={busy} busyText="Menerbitkan PO..."
                        data-testid="po-assign-submit"
                        className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Tetapkan vendor &amp; terbitkan PO
                </BusyButton>
              </div>
            </>
          )}
        </Card>
      )}

      {/* ===== Ringkasan ===== */}
      <Card className="p-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          <div>
            <div className="text-muted-foreground">Vendor</div>
            <div className="font-medium">
              {po.vendor?.name || <span className="text-warning-soft-foreground italic">belum ditetapkan</span>}
              {po.vendor && po.vendor.is_active === false && (
                <span className="ml-2 text-xs text-muted-foreground">(nonaktif)</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Dari PR</div>
            <div>
              {po.pr?.pr_number ? (
                <button type="button" data-testid="po-pr-link"
                        onClick={() => navigate(`/purchasing/pr/${po.pr.id}`)}
                        className="font-medium text-primary underline underline-offset-2 hover:opacity-80 inline-flex items-center gap-1">
                  <FileText className="h-4 w-4" /> {po.pr.pr_number}
                </button>
              ) : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Wilayah / Cabang</div>
            <div className="font-medium">
              {po.region?.name || "—"}{po.store?.name ? ` · ${po.store.name}` : ""}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Tanggal PO</div>
            <div className="font-medium">{formatDate(po.po_date, tzForRegion(po.region_id || po.region?.id))}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Perkiraan kirim</div>
            <div className="font-medium">{formatDate(po.expected_delivery_date, tzForRegion(po.region_id || po.region?.id))}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Nilai PO</div>
            <div className="font-medium">{formatIDR(po.total_amount)}</div>
          </div>
          {po.vendor_assigned_at && (
            <div>
              <div className="text-muted-foreground">Vendor ditetapkan</div>
              <div>{formatDateTime(po.vendor_assigned_at, tzForRegion(po.region_id || po.region?.id))}</div>
            </div>
          )}
          {po.closed_at && (
            <div>
              <div className="text-muted-foreground">Ditutup</div>
              <div>{formatDateTime(po.closed_at, tzForRegion(po.region_id || po.region?.id))}</div>
            </div>
          )}
          {po.completed_at && (
            <div>
              <div className="text-muted-foreground">Selesai</div>
              <div>{formatDateTime(po.completed_at, tzForRegion(po.region_id || po.region?.id))}</div>
            </div>
          )}
        </div>
        {po.cancel_reason && (
          <div className="mt-3 text-sm bg-danger-soft text-danger-soft-foreground border border-danger/30 rounded p-3"
               data-testid="po-cancel-reason">
            <strong>Alasan pembatalan:</strong> {po.cancel_reason}
          </div>
        )}
        {po.notes && (
          <div className="mt-3 text-sm text-muted-foreground">
            <span className="text-muted-foreground">Catatan:</span> {po.notes}
          </div>
        )}
      </Card>

      {/* ===== Item ===== */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/50 text-sm font-medium">
          Item PO ({(po.items || []).length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Bahan</th>
                <th className="px-4 py-2 text-right font-medium">Dipesan</th>
                <th className="px-4 py-2 text-right font-medium">Diterima</th>
                <th className="px-4 py-2 text-right font-medium">Harga satuan</th>
                <th className="px-4 py-2 text-right font-medium">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(po.items || []).map(it => (
                <tr key={it.id} data-testid={`po-item-${it.id}`}>
                  <td className="px-4 py-2">
                    {materialLabel(it)}
                    {it.notes && <div className="text-xs text-muted-foreground">{it.notes}</div>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {formatQty(it.qty_ordered, it.unit_of_measure)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {formatQty(it.qty_received ?? 0, it.unit_of_measure)}
                  </td>
                  <td className="px-4 py-2 text-right">{formatIDR(it.unit_price)}</td>
                  <td className="px-4 py-2 text-right">{formatIDR(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ===== Penerimaan barang (tautan dua arah GRN <-> PO) ===== */}
      <Card className="overflow-hidden" data-testid="po-grn-panel">
        <div className="px-4 py-3 border-b bg-muted/50 flex items-center
                        justify-between gap-2 flex-wrap">
          <span className="text-sm font-medium">
            Penerimaan barang ({grns.length})
          </span>
          {grnPerm.receive && ["ISSUED", "PARTIAL_RECEIVED"].includes(po.status) && (
            <Button size="sm" data-testid="po-receive-btn"
                    onClick={() => navigate(`/purchasing/receiving?po=${po.id}`)}>
              Terima barang
            </Button>
          )}
        </div>
        {grns.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground" data-testid="po-grn-empty">
            {["ISSUED", "PARTIAL_RECEIVED"].includes(po.status)
              ? "Belum ada barang yang diterima untuk PO ini. Penerimaan dicatat Admin Gudang lewat tombol \u2018Terima barang\u2019 di atas."
              : "Tidak ada penerimaan tercatat untuk PO ini."}
          </div>
        ) : (
          <div className="divide-y">
            {grns.map(gr => (
              <div key={gr.id} className="px-4 py-2 text-sm flex items-center
                                          justify-between gap-2 flex-wrap"
                   data-testid={`po-grn-${gr.receipt_number}`}>
                <button type="button"
                        onClick={() => navigate("/purchasing/receiving")}
                        className="font-medium text-primary underline underline-offset-2 hover:opacity-80">
                  {gr.receipt_number}
                </button>
                <span className="text-muted-foreground">
                  {formatDate(gr.receipt_date, tzForRegion(po.region_id || po.region?.id))}
                  {gr.store?.name ? ` · ${gr.store.name}` : ""}
                  {gr.status === "CANCELLED" ? " · Dibatalkan" : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ===== Tagihan vendor (tautan dua arah invoice <-> PO, #4b-3a) =====
          Panel ini hanya muncul untuk role yang memang boleh membaca tagihan
          (INVOICE_READ_ROLES). Sinyalnya `invPerm.read` dari /meta/invoice —
          untuk role lain permintaan meta-nya 403 dan panel tidak dirender,
          jadi tidak ada nilai tagihan yang bocor ke layar mereka. */}
      {invPerm.read && (
        <Card className="overflow-hidden" data-testid="po-invoice-panel">
          <div className="px-4 py-3 border-b bg-muted/50 flex items-center
                          justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium">
              Tagihan vendor ({invoices.length})
            </span>
            {invPerm.create && invEligible.includes(po.status) && (
              <Button size="sm" data-testid="po-create-invoice-btn"
                      onClick={() => navigate(`/purchasing/invoices/new?po_id=${po.id}`)}>
                Buat Tagihan
              </Button>
            )}
          </div>
          {invoices.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground" data-testid="po-invoice-empty">
              {invEligible.includes(po.status)
                ? <>Belum ada tagihan untuk PO ini.{" "}<Hint>Tagihan diajukan Admin Purchasing, lalu disetujui Kepala Cabang/Owner sebelum Finance membayar.</Hint></>
                : "Tidak ada tagihan tercatat untuk PO ini."}
            </div>
          ) : (
            <div className="divide-y">
              {invoices.map(iv => (
                <div key={iv.id} className="px-4 py-2 text-sm flex items-center
                                            justify-between gap-2 flex-wrap"
                     data-testid={`po-invoice-${iv.internal_number}`}>
                  <button type="button"
                          onClick={() => navigate(`/purchasing/invoices/${iv.id}`)}
                          className="font-medium text-primary underline underline-offset-2 hover:opacity-80">
                    {iv.internal_number}
                  </button>
                  <span className="text-muted-foreground">
                    {formatDate(iv.invoice_date, tzForRegion(po.region_id || po.region?.id))}
                    {" · "}{formatIDR(iv.total_amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ===== Uang muka PO ini (tautan dua arah DP <-> PO, #4b-3c) =====
          Menampilkan SISA PLAFON, bukan cuma daftar: itulah angka yang
          menentukan apakah pengajuan berikutnya akan diterima, dan ia harus
          terbaca SEBELUM tombol ditekan. Rasio dan daftar status yang
          menahan plafon dari /meta/dp — nol konstanta di berkas ini. */}
      {(dpMeta?.permissions || {}).read && (
        <Card className="overflow-hidden" data-testid="po-dp-panel">
          <div className="px-4 py-3 border-b bg-muted/50 flex items-center
                          justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium">
              Uang muka PO ini ({dps.length})
            </span>
            {(dpMeta.permissions || {}).create
              && (dpMeta.eligibility?.po_statuses_for_new_dp || []).includes(po.status) && (
              <Button size="sm" data-testid="po-create-dp-btn"
                      onClick={() => navigate(`/purchasing/dp?po_id=${po.id}`)}>
                Ajukan Uang Muka
              </Button>
            )}
          </div>

          {/* Plafon dihitung dari daftar yang sudah di tangan — nol
              panggilan tambahan. */}
          {(() => {
            const plafon = hitungPlafon(dpMeta, po, dps);
            if (!plafon || plafon.tidakDiketahui) return null;
            return (
              <dl className="px-4 py-3 border-b grid grid-cols-3 gap-x-4 text-sm"
                  data-testid="po-dp-plafon">
                <div>
                  <dt className="text-muted-foreground text-xs">
                    Plafon ({Math.round(dpMeta.limits.cap_ratio * 100)}% nilai PO)
                  </dt>
                  <dd>{formatIDR(plafon.plafon)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Sudah terpakai</dt>
                  <dd>{formatIDR(plafon.terpakai)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">Sisa</dt>
                  <dd className="font-semibold" data-testid="po-dp-sisa">
                    {formatIDR(Math.max(plafon.sisa, 0))}
                  </dd>
                </div>
              </dl>
            );
          })()}

          {dps.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground" data-testid="po-dp-empty">
              {(dpMeta.eligibility?.po_statuses_for_new_dp || []).includes(po.status)
                ? <>Belum ada uang muka untuk PO ini.{" "}<Hint>Uang muka diajukan Admin Purchasing, disetujui Kepala Cabang/Owner, lalu dibayar Finance.</Hint></>
                : "Tidak ada uang muka tercatat untuk PO ini."}
            </div>
          ) : (
            <div className="divide-y">
              {dps.map(d => {
                const bd = dpStatusBadge(dpMeta, d.status);
                return (
                  <div key={d.id} className="px-4 py-2 text-sm flex items-center
                                             justify-between gap-2 flex-wrap"
                       data-testid={`po-dp-${d.dp_number}`}>
                    <button type="button"
                            onClick={() => navigate(`/purchasing/dp/${d.id}`)}
                            className="font-medium text-primary underline underline-offset-2 hover:opacity-80">
                      {d.dp_number}
                    </button>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-muted-foreground">
                        {formatDate(d.dp_date, tzForRegion(po.region_id || po.region?.id))}
                        {" · "}{formatIDR(d.amount)}
                      </span>
                      {d.is_aging && (
                        <StatusBadge tone="danger">
                          Menggantung {d.age_days} hari
                        </StatusBadge>
                      )}
                      <StatusBadge tone={bd.tone} title={bd.description}>
                        {bd.label}
                      </StatusBadge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* ===== Aksi status ===== */}
      <div className="flex gap-2 flex-wrap justify-end">
        {canClose && (
          <BusyButton busy={busy} busyText="Menutup PO..." data-testid="po-close-btn"
                  className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90"
                  onClick={() => runAction(() => closePO(id), "PO ditutup.",
                                           "Gagal menutup PO.")}>
            Tutup PO
          </BusyButton>
        )}
        {canPRSisa && (
          <BusyButton busy={busy} busyText="Membuat PR..." data-testid="po-pr-sisa-btn" variant="outline"
                  onClick={() => runAction(async () => {
                    const r = await buatPRSisa(id);
                    if (r?.data?.id) navigate(`/purchasing/pr/${r.data.id}`);
                    return r;
                  }, "PR sisa dibuat — lanjutkan lewat alur PR biasa.", "Gagal membuat PR sisa.")}>
            Buat PR sisa ({sisaItems.length} item)
          </BusyButton>
        )}
        {canAjukanTS && (
          <Button variant="outline" disabled={busy} data-testid="po-tutup-sebagian-btn"
                  onClick={async () => {
                    const alasan = await mintaTeks(
                      "Alasan menutup sebagian (sisa tidak akan datang). Kepala Cabang yang menyetujui:", { minPanjang: 5 });
                    if (alasan) runAction(() => ajukanTutupSebagian(id, alasan), "Pengajuan tutup sebagian dikirim ke Kepala Cabang.",
                                          "Gagal mengajukan tutup sebagian.");
                  }}>
            Ajukan tutup sebagian
          </Button>
        )}
        {canAjukanBT && (
          <Button variant="outline" disabled={busy} data-testid="po-batal-telat-btn"
                  onClick={async () => {
                    const alasan = await mintaTeks(
                      btSisa
                        ? "Alasan membatalkan sisa barang karena vendor telat (mulai hari kerja ke-8 dari janji kirim). Barang yang sudah diterima tetap; Kepala Cabang yang menyetujui; sesudahnya pesan sisanya ke vendor lain:"
                        : "Alasan membatalkan PO karena vendor telat (mulai hari kerja ke-8 dari janji kirim). Kepala Cabang yang menyetujui; sesudahnya buat PO baru ke vendor lain:",
                      { minPanjang: 5 });
                    if (alasan) runAction(() => ajukanBatalTelat(id, alasan), "Pengajuan batal dikirim ke Kepala Cabang.",
                                          "Gagal mengajukan batal.");
                  }}>
            {btSisa ? "Ajukan batal sisa (vendor telat)" : "Ajukan batal (vendor telat)"}
          </Button>
        )}
        {canAjukanBatal && (
          <Button variant="outline" disabled={busy} data-testid="po-ajukan-batal-btn"
                  onClick={async () => {
                    const alasan = await mintaTeks(
                      "Alasan membatalkan PO ini (mis. salah vendor, salah harga, bahan tidak jadi dibutuhkan). PO sudah dikirim ke vendor, jadi Kepala Cabang cabang PO yang menyetujui:",
                      { minPanjang: 5 });
                    if (alasan) runAction(() => ajukanBatalPO(id, alasan), "Pengajuan pembatalan dikirim ke Kepala Cabang.",
                                          "Gagal mengajukan pembatalan.");
                  }}>
            Ajukan pembatalan
          </Button>
        )}
        {canCancel && (
          <Button variant="outline" disabled={busy} data-testid="po-cancel-btn"
                  onClick={() => { setReason(""); setCancelOpen(true); }}>
            Batalkan PO
          </Button>
        )}
      </div>

      {tutupSebagian.length > 0 && (
        <Card className="p-4 space-y-2 text-sm" data-testid="po-tutup-sebagian-panel">
          <div className="font-medium">Tutup sebagian</div>
          {tutupSebagian.map((t) => (
            <div key={t.id} className="border-t pt-2 space-y-1">
              <div>
                <b>{({ DIAJUKAN: "Menunggu Kepala Cabang", DISETUJUI: "Disetujui", DITOLAK: "Ditolak", DIBATALKAN: "Dibatalkan" })[t.status] || t.status}</b>
                {" · "}{formatDateTime(t.diajukan_pada)} · {t.alasan}
              </div>
              <div className="text-xs text-muted-foreground">
                Barang diterima {formatIDR(t.nilai_diterima_idr)} · uang muka dibayar {formatIDR(t.dp_dibayar_idr)}
                {Number(t.dp_lebih_idr) > 0 && <> · <span className="text-destructive">kelebihan uang muka {formatIDR(t.dp_lebih_idr)} — Finance menyelesaikannya sebagai REFUND (tagihan balik ke vendor) sebelum disetujui</span></>}
              </div>
              {t.alasan_tolak && <div className="text-xs">Alasan tolak: {t.alasan_tolak}</div>}
              {t.status === "DIAJUKAN" && (
                <div className="flex gap-2">
                  {perm.tutup_sebagian_putus && (
                    <>
                      <Button size="sm" disabled={busy} data-testid={`po-ts-setuju-${t.id}`}
                              onClick={() => runAction(() => putusTutupSebagian(t.id, true, null),
                                                       "Sisa barang ditutup. PO dianggap selesai sesudah tagihannya lunas.",
                                                       "Gagal menyetujui.")}>Setujui</Button>
                      <Button size="sm" variant="outline" disabled={busy} data-testid={`po-ts-tolak-${t.id}`}
                              onClick={async () => {
                                const alasan = await mintaTeks("Alasan menolak:", { minPanjang: 5 });
                                if (alasan) runAction(() => putusTutupSebagian(t.id, false, alasan), "Pengajuan ditolak.",
                                                      "Gagal menolak.");
                              }}>Tolak</Button>
                    </>
                  )}
                  {perm.tutup_sebagian_ajukan && (
                    <Button size="sm" variant="ghost" disabled={busy} data-testid={`po-ts-batal-${t.id}`}
                            onClick={() => runAction(() => batalkanTutupSebagian(t.id), "Pengajuan dibatalkan.",
                                                     "Gagal membatalkan.")}>Batalkan</Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {batalTelat.length > 0 && (
        <Card className="p-4 space-y-2 text-sm" data-testid="po-batal-telat-panel">
          <div className="font-medium">Pengajuan pembatalan</div>
          {batalTelat.map((t) => (
            <div key={t.id} className="border-t pt-2 space-y-1">
              <div>
                <b>{({ DIAJUKAN: "Menunggu Kepala Cabang",
                       DISETUJUI: t.efek === "TUTUP_SISA" ? "Disetujui — sisa barang dibatalkan, yang sudah diterima tetap" : "Disetujui — PO dibatalkan",
                       DITOLAK: "Ditolak", DIBATALKAN: "Dibatalkan" })[t.status] || t.status}</b>
                {" · "}{t.jenis === "BIASA" ? "Pembatalan biasa" : "Vendor telat"}
                {" · "}{formatDateTime(t.diajukan_pada)} · {t.alasan}
              </div>
              {t.jenis !== "BIASA" && (
                <div className="text-xs text-muted-foreground">
                  Janji kirim {formatDate(t.janji_kirim)} · telat {t.hari_telat} hari ({t.hari_kerja_telat} hari kerja) saat diajukan
                </div>
              )}
              {t.alasan_tolak && <div className="text-xs">Alasan tolak: {t.alasan_tolak}</div>}
              {t.status === "DIAJUKAN" && t.efek === "TUTUP_SISA" && (
                <div className="text-xs">Sebagian barang sudah diterima — bila disetujui, hanya sisanya yang dibatalkan.
                  Tagihan barang yang sudah diterima tetap dibayar Finance; PO selesai begitu tagihannya lunas.</div>
              )}
              {t.status === "DISETUJUI" && (
                <div className="text-xs">
                  {t.efek === "TUTUP_SISA" ? "Pesan sisanya ke vendor lain" : "Buat PO baru ke vendor lain"} lewat
                  Permintaan Pembelian (PR) seperti biasa.
                </div>
              )}
              {t.status === "DIAJUKAN" && (
                <div className="flex gap-2">
                  {(perm.batal_telat_putus || perm.batal_putus) && (
                    <>
                      <Button size="sm" disabled={busy} data-testid={`po-bt-setuju-${t.id}`}
                              onClick={() => runAction(() => putusBatalTelat(t.id, true, null),
                                                       t.efek === "TUTUP_SISA" ? "Sisa barang dibatalkan. PO dianggap selesai sesudah tagihannya lunas." : "PO dibatalkan.",
                                                       "Gagal menyetujui.")}>Setujui</Button>
                      <Button size="sm" variant="outline" disabled={busy} data-testid={`po-bt-tolak-${t.id}`}
                              onClick={async () => {
                                const alasan = await mintaTeks("Alasan menolak:", { minPanjang: 5 });
                                if (alasan) runAction(() => putusBatalTelat(t.id, false, alasan), "Pengajuan ditolak.",
                                                      "Gagal menolak.");
                              }}>Tolak</Button>
                    </>
                  )}
                  {(perm.batal_telat_ajukan || perm.batal_ajukan) && (
                    <Button size="sm" variant="ghost" disabled={busy} data-testid={`po-bt-batal-${t.id}`}
                            onClick={() => runAction(() => batalkanBatalTelat(t.id), "Pengajuan dibatalkan.",
                                                     "Gagal membatalkan.")}>Batalkan</Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {po.status === "FULLY_RECEIVED" && perm.close && (
        <p className="text-xs text-muted-foreground text-right -mt-4" data-testid="po-close-hint">
          Menutup PO bersifat final dan tidak bisa dibuka lagi. Sistem menolak
          menutup PO yang tagihan aktifnya belum lunas.
        </p>
      )}

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="po-cancel-dialog">
          <DialogHeader><DialogTitle>Batalkan PO {po.po_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            PO yang punya penerimaan barang aktif tidak bisa dibatalkan — batalkan penerimaannya dulu.
          </p>
          <label className="block">
            <span className="text-sm">Alasan <span aria-hidden="true">*</span> (min. 3 huruf)</span>
            <Textarea value={reason} rows={3} className="mt-1"
                      data-testid="po-cancel-reason-input"
                      onChange={(e) => setReason(e.target.value)} />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={busy}>Batal</Button>
            <BusyButton variant="destructive" busy={busy} disabled={reason.trim().length < 3}
                    busyText="Membatalkan..." data-testid="po-cancel-confirm"
                    onClick={() => runAction(() => cancelPO(id, { reason: reason.trim() }),
                                             "PO dibatalkan.", "Gagal membatalkan PO.")}>
              Batalkan PO
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
